import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { IMDFData, LayerControl } from '../../types/imdf.types';

// 修復 Leaflet 預設圖標問題
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface EnhancedLeafletMapProps {
  imdfData?: IMDFData | null;
  layerControl: LayerControl;
  onLayerControlChange: (layerControl: LayerControl) => void;
  selectedFeature?: GeoJSON.Feature | null;
  onFeatureClick?: (feature: GeoJSON.Feature) => void;
  height?: string;
  width?: string;
  className?: string;
}

const EnhancedLeafletMap: React.FC<EnhancedLeafletMapProps> = ({
  imdfData,
  layerControl,
  onLayerControlChange,
  selectedFeature,
  onFeatureClick,
  height = '500px',
  width = '100%',
  className = '',
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const layerGroupsRef = useRef<{ [key: string]: L.LayerGroup }>({});
  const [mapReady, setMapReady] = useState(false);

  // 圖層樣式配置
  const getLayerStyle = useCallback((feature: GeoJSON.Feature) => {
    const sourceFile = feature.properties?._sourceFile || '';
    const category = feature.properties?.category || '';
    
    // 根據檔案名稱或 category 決定樣式
    if (sourceFile.includes('venue') || category.toLowerCase().includes('venue')) {
      return { color: '#8B5CF6', weight: 3, fillOpacity: 0.2, fillColor: '#8B5CF6' };
    } else if (sourceFile.includes('building') || category.toLowerCase().includes('building')) {
      return { color: '#EF4444', weight: 2, fillOpacity: 0.3, fillColor: '#EF4444' };
    } else if (sourceFile.includes('level') || category.toLowerCase().includes('level')) {
      return { color: '#3B82F6', weight: 1, fillOpacity: 0.2, fillColor: '#3B82F6' };
    } else if (sourceFile.includes('unit') || category.toLowerCase().includes('unit')) {
      return { color: '#10B981', weight: 1, fillOpacity: 0.4, fillColor: '#10B981' };
    } else if (sourceFile.includes('amenity')) {
      return { color: '#F59E0B', weight: 1, fillOpacity: 0.5, fillColor: '#F59E0B' };
    } else if (sourceFile.includes('opening')) {
      return { color: '#EC4899', weight: 1, fillOpacity: 0.3, fillColor: '#EC4899' };
    } else if (sourceFile.includes('fixture')) {
      return { color: '#6366F1', weight: 1, fillOpacity: 0.4, fillColor: '#6366F1' };
    } else if (sourceFile.includes('anchor')) {
      return { color: '#06B6D4', weight: 3, fillOpacity: 0.8, fillColor: '#06B6D4' };
    }
    
    return { color: '#6B7280', weight: 2, fillOpacity: 0.3, fillColor: '#6B7280' };
  }, []);

  // 取得圖層類型
  const getLayerType = useCallback((feature: GeoJSON.Feature): string => {
    const sourceFile = feature.properties?._sourceFile || '';
    
    if (sourceFile.includes('venue')) return 'venue';
    if (sourceFile.includes('building')) return 'building';
    if (sourceFile.includes('level')) return 'level';
    if (sourceFile.includes('unit')) return 'unit';
    if (sourceFile.includes('amenity')) return 'amenity';
    if (sourceFile.includes('opening')) return 'opening';
    if (sourceFile.includes('fixture')) return 'fixture';
    if (sourceFile.includes('anchor')) return 'anchor';
    
    return 'other';
  }, []);

  // 初始化地圖
  useEffect(() => {
    if (!mapRef.current) return;

    mapInstanceRef.current = L.map(mapRef.current, {
      center: [25.033, 121.565], // 台北市中心
      zoom: 13,
      maxZoom: 19,
    });

    // 添加基礎圖層
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapInstanceRef.current);

    setMapReady(true);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // 更新 IMDF 圖層
  useEffect(() => {
    if (!mapInstanceRef.current || !imdfData || !mapReady) return;

    // 清除現有圖層
    Object.values(layerGroupsRef.current).forEach(layerGroup => {
      mapInstanceRef.current?.removeLayer(layerGroup);
    });
    layerGroupsRef.current = {};

    // 根據檔案類型創建圖層組
    const layerGroups: { [key: string]: L.LayerGroup } = {};

    imdfData.files.forEach(file => {
      file.features.forEach(feature => {
        const layerType = getLayerType(feature);
        
        if (!layerGroups[layerType]) {
          layerGroups[layerType] = L.layerGroup();
        }

        // 創建 GeoJSON 圖層
        const geoJsonLayer = L.geoJSON(feature, {
          style: () => getLayerStyle(feature),
          onEachFeature: (feature, layer) => {
            // 綁定點擊事件
            layer.on('click', () => {
              if (onFeatureClick) {
                onFeatureClick(feature);
              }
            });

            // 綁定 popup
            const name = feature.properties?.name || feature.id || 'Unknown';
            const category = feature.properties?.category || 'Unknown';
            const stoConfirm = feature.properties?.stoConfirm ? '✅' : '❌';
            
            layer.bindPopup(`
              <div class="p-2">
                <h4 class="font-semibold">${name}</h4>
                <p class="text-sm text-gray-600">分類: ${category}</p>
                <p class="text-sm text-gray-600">確認狀態: ${stoConfirm}</p>
                <p class="text-xs text-gray-500">ID: ${feature.id || 'N/A'}</p>
              </div>
            `);
          },
        });

        layerGroups[layerType].addLayer(geoJsonLayer);
      });
    });

    // 保存圖層組引用並根據控制狀態添加到地圖
    layerGroupsRef.current = layerGroups;
    Object.entries(layerGroups).forEach(([layerType, layerGroup]) => {
      if (layerControl[layerType]) {
        layerGroup.addTo(mapInstanceRef.current!);
      }
    });

    // 自動調整地圖視野
    if (imdfData.combinedGeoJson && imdfData.combinedGeoJson.features.length > 0) {
      const bounds = L.geoJSON(imdfData.combinedGeoJson).getBounds();
      if (bounds.isValid()) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [10, 10] });
      }
    }

  }, [imdfData, mapReady, getLayerType, getLayerStyle, onFeatureClick]);

  // 更新圖層顯示狀態
  useEffect(() => {
    if (!mapInstanceRef.current || !mapReady) return;

    Object.entries(layerGroupsRef.current).forEach(([layerType, layerGroup]) => {
      if (layerControl[layerType]) {
        if (!mapInstanceRef.current!.hasLayer(layerGroup)) {
          layerGroup.addTo(mapInstanceRef.current!);
        }
      } else {
        if (mapInstanceRef.current!.hasLayer(layerGroup)) {
          mapInstanceRef.current!.removeLayer(layerGroup);
        }
      }
    });
  }, [layerControl, mapReady]);

  // 處理選中 feature 的高亮顯示
  useEffect(() => {
    if (!mapInstanceRef.current || !selectedFeature || !mapReady) return;

    // 這裡可以添加高亮選中 feature 的邏輯
    // 例如縮放到該 feature 的位置
    if (selectedFeature.geometry) {
      const tempLayer = L.geoJSON(selectedFeature);
      const bounds = tempLayer.getBounds();
      if (bounds.isValid()) {
        mapInstanceRef.current.fitBounds(bounds, { maxZoom: 16, padding: [20, 20] });
      }
    }
  }, [selectedFeature, mapReady]);

  // 圖層控制按鈕
  const layerControlButtons = [
    { key: 'venue', label: 'Venue', color: '#8B5CF6' },
    { key: 'building', label: 'Building', color: '#EF4444' },
    { key: 'level', label: 'Level', color: '#3B82F6' },
    { key: 'unit', label: 'Unit', color: '#10B981' },
    { key: 'amenity', label: 'Amenity', color: '#F59E0B' },
    { key: 'opening', label: 'Opening', color: '#EC4899' },
    { key: 'fixture', label: 'Fixture', color: '#6366F1' },
    { key: 'anchor', label: 'Anchor', color: '#06B6D4' },
  ];

  return (
    <div className={`relative ${className}`}>
      {/* 圖層控制面板 */}
      <div className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg p-3">
        <h4 className="text-sm font-medium text-gray-900 mb-2">圖層控制</h4>
        <div className="space-y-2">
          {layerControlButtons.map(({ key, label, color }) => (
            <label key={key} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={layerControl[key] || false}
                onChange={(e) => {
                  onLayerControlChange({
                    ...layerControl,
                    [key]: e.target.checked,
                  });
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: color }}
              ></div>
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* 地圖容器 */}
      <div
        ref={mapRef}
        style={{ height, width }}
        className="rounded-lg overflow-hidden"
      />
      
      {/* 載入指示器 */}
      {!mapReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">載入地圖中...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedLeafletMap;